import { Dayjs } from "dayjs";
import { ShootingLocationInterface } from "./ShootingLocation";
import { AllocationDetails } from "./ticketingDashboard/AllocationDetails";

export type AllocationType = "Generic" | "VIP" | "FriendsAndFamily" | "Priority" | "AccessibilityRequirements";

export enum AllocationTypeEnum {
  Generic = "generic",
  VIP = "vip",
  FriendsAndFamily = "friendsAndFamily",
  Priority = "priority",
  AccessibilityRequirements = "accessibilityRequirements"
}

export interface AllocationsPayload {
  parentAllocationId: number | null;
  showId: number;
  allocations: AllocationData[];
}

export interface AllocationData {
  id?: number;
  name: string;
  type: string;
  deadlines: DeadlineData[],
  tickets: TicketData[],
  users: UserData[]
}

export interface DeadlineData {
  sessionId: number;
  deadline: number;
  operatingStatus: OperatingStatus;
}

export interface TicketData {
  sessionId: number;
  numberOfTickets: number;
  operatingStatus: OperatingStatus;
}

export interface UserData {
  // only for new users
  firstName: string | null;
  lastName: string | null;
  email: string | null;

  // only for existing users
  userId: string | null;

  canSubAllocate: boolean;
  isReadOnly: boolean;
  operatingStatus: OperatingStatus;
}

export type OperatingStatus = "ExistedAndNoChange" | "ExistedAndChanged" | "ExistedAndDeleted" | "New";

export interface ShowDetails {
  id: number;
  name:	string;
  imageUrl: string;
  publicRouteUrl: string;
  nextSessionDeadline: Dayjs | undefined;
  nextSessionName: string | undefined;
  nextSessionId: number | undefined;
  nextSessionMinimumAgeRequirements: number | undefined;
  relativeRootAllocation: ShowAllocation;
  sessions: ShowSession[];
  defaultMinimumAgeRequirement: number | undefined;
  allocations: ShowAllocation[];
  showInformation: ShowInformation[];
}

export interface AllocationCalculation {
  allocatedTicketsCount: number;
  availableBookingInterestsCount: number;
  pendingBookingInterestsCount: number;
  acceptedBookingInterestsCount: number;
  declinedBookingInterestsCount: number;
  totalAvailableBookingInterestsCount?: number;
  totalPendingBookingInterestsCount?: number;
  totalAcceptedBookingInterestsCount?: number;
  totalDeclinedBookingInterestsCount?: number;
  subAllocatedSeatsCount: number;
}

export interface ShowAllocation {
  id: number;
  name: string;
  type: AllocationType;
  magicLink?: string;
  allocationUsers: AllocationUser[];
  allocationDetails: AllocationDetails;
  allocatedTicketsCount: number;
  availableBookingInterestsCount: number;
  pendingBookingInterestsCount: number;
  acceptedBookingInterestsCount: number;
  declinedBookingInterestsCount: number;
  sessionAllocationCalculations: { [key: number]: AllocationCalculation };
  isDeletable: boolean;
}

export interface AllocationUser {
  userId: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  canSubAllocate: boolean;
  isReadOnly: boolean;
  canSendTickets: boolean;
}

export interface AllocationSession {
  allocationId: number,
  deadlineOffset: number
}

export interface ShowSession {
  id: number;
  name: string;
  maximumSeatingCapacity: number;
  allocatedSeatsCount: number;
  acceptedBookedSeatsWithOutAllocation: number;
  pendingBookedSeatsWithOutAllocation: number;
  declinedBookedSeatsWithOutAllocation: number;
  usedAllocatedSeatsCount: number;
  subAllocatedSeatsCount: number;
  shootingLocation?: ShootingLocationInterface | null;
  arrivalTime: Dayjs;
  showStartTime: Dayjs;
  approxFinishTime: Dayjs;
  overrideMinimumAgeRequirement?: number;
  allocationSessions?: AllocationSession[]
}

export interface PriorityAllocationSessionStatus {
  isFull: boolean;
  availableSeats: number;
}

export interface PriorityAllocationCheckResponse {
  [x: string]: PriorityAllocationSessionStatus;
}
export interface UpdateAllocationResult {
  allocationId: number;
  allocationName: string;
  error: string;
  isSuccessful: boolean;
  validationResults: []
}

export interface ShowInformation {
  sessionId: number;
  isMaximumSeatingCapacityConfirmed: boolean;
  maximumSeatingCapacity: number;
  generalTicketsCount: number;
  friendsAndFamilyTicketsCount: number;
  vipTicketsCount: number;
  priorityTicketsCount: number;
  accessibilityTicketsCount: number;
}